const DEFAULT_WEB_DOCUMENT_TITLE = 'Bloom Holding';

/**
 * Use a Contentful locale value when it resolved.
 * `t()` returns the key path when the string is missing.
 */
export function preferContentfulCopy(
  value: string,
  key: string,
  fallback: string,
): string {
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed === key) {
    return fallback;
  }
  return trimmed;
}
const META_DESCRIPTION_MAX_LENGTH = 160;

export interface WebPathTitleCopy {
  readonly welcomeTitle: string;
  readonly exploreBloomWorld: string;
  readonly exploreProperties: string;
  readonly projectDetails: string;
  readonly faq: string;
  /** True when `/` redirects to the Bloom Universal hub. */
  readonly universalLanding: boolean;
}

/**
 * Title for the first HTML response, before route screens mount.
 * Screens replace this once they render.
 */
export function webDocumentTitleForPath(
  pathname: string | undefined,
  copy: WebPathTitleCopy,
): string | undefined {
  const path = normalizeWebPath(pathname);
  if (path === '/') {
    return copy.universalLanding ? copy.exploreBloomWorld : copy.welcomeTitle;
  }
  if (path === '/welcome') {
    return copy.welcomeTitle;
  }
  if (path === '/universal' || path === '/bloom-universal') {
    return copy.exploreBloomWorld;
  }
  if (path === '/guest') {
    return copy.exploreProperties;
  }
  if (path === '/guest-details') {
    return copy.projectDetails;
  }
  if (path === '/faq' || path.startsWith('/faq-category')) {
    return copy.faq;
  }
  return undefined;
}

export function normalizeWebPath(pathname: string | undefined): string {
  const withoutQuery = (pathname ?? '').split('?')[0] ?? '';
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery.length === 0 ? '/' : withoutQuery;
}

/**
 * Browser tab / SEO title. Page titles are suffixed with the app name.
 * A page title that matches the app name is returned once.
 */
export function formatWebDocumentTitle(
  pageTitle: string | undefined,
  appName: string | undefined,
): string {
  const app = appName?.trim() || DEFAULT_WEB_DOCUMENT_TITLE;
  const page = pageTitle?.trim() ?? '';
  if (page.length === 0 || page === app) {
    return app;
  }
  return `${page} | ${app}`;
}

/** Single-line meta description, capped for search snippets. */
export function formatWebMetaDescription(description: string | undefined): string {
  const normalized = (description ?? '').replace(/\s+/g, ' ').trim();
  if (normalized.length <= META_DESCRIPTION_MAX_LENGTH) {
    return normalized;
  }
  const sliced = normalized.slice(0, META_DESCRIPTION_MAX_LENGTH - 1).trimEnd();
  return `${sliced}…`;
}
